import { useEffect, useRef, useState } from 'react';
import { analyzeDateBirth } from '../../../scripts/analyzeDateBirth';
import DocumentValidator from '../../../scripts/documentValidator';
import { sanitizeClientSessionData } from '../../../scripts/sanitizeClientSessionData';
import axios from '../../../services/axios';
import { checkCommandStatus } from '../services/checkCommandStatus';
import { generateValidateInformation } from '../services/generateValidateInformation';
import { restrictionController } from '../services/restrictionController';
import { saveOrder } from '../services/saveOrder';
import { validateOrder } from '../services/validateOrder';
import { validateUUID } from '../services/validateUUID';


// Controla o fluxo de identificação do cliente e finalização do pedido.
//
// Passo a passo (nem todo pedido passa por todas as etapas):
//   1. COMANDA: pede o CPF (requireDocumentAlert) e busca a comanda pelo documento.
//      - Comanda encontrada -> segue direto para makeOrder.
//      - Cliente não cadastrado -> registerAlert (cadastrar ou só informar o nome).
//      - Cliente cadastrado sem comanda aberta -> analyzeData.
//   2. analyzeData decide a comanda do cliente:
//      - Pergunta a quantidade de pessoas na mesa uma única vez por sessão (peopleCountAlert).
//      - Se o dispositivo ainda não validou esse cliente, pede confirmação por QR-Code (validateInfoAlert).
//      - Cria/reaproveita a comanda no backend e chama makeOrder.
//   3. makeOrder valida a comanda, filtra produtos restritos (18+) e envia o pedido.
export function useOrderFlow({
    storage,
    navigate,
    companyData,
    location,
    clientArray,
    cart,
    products,
    setClientData,
    setLoading,
    handleDeleteProduct,
    clearCart,
}) {
    // COMANDA (BUSCA POR CPF)
    const [requireDocumentAlert, setRequireDocumentAlert] = useState(false);
    const [clientDocument, setClientDocument] = useState('');

    // CLIENTE NÃO ENCONTRADO -> CADASTRO
    const [registerAlert, setRegisterAlert] = useState(false);
    const [clientNameAlert, setClientNameAlert] = useState(false);
    const [clientName, setClientName] = useState(null);
    const [olderAlert, setOlderAlert] = useState(false);

    // TIPO DE PEDIDO (delivery já funciona; retirada aguarda o módulo dedicado)
    const [orderTypeAlert, setOrderTypeAlert] = useState(false);
    const [orderTypeValue, setOrderTypeValue] = useState('COMMAND');
    const [clientPhoneAlert, setClientPhoneAlert] = useState(false);
    const [clientPhone, setClientPhone] = useState(null);

    // QUANTIDADE DE PESSOAS NA COMANDA (perguntada uma única vez por sessão de carrinho)
    const [peopleCount, setPeopleCount] = useState(1);
    const [peopleCountAlert, setPeopleCountAlert] = useState(false);
    const peopleCountAsked = useRef(false);
    const pendingAnalyzeClient = useRef(null);

    // VALIDAÇÃO DO DISPOSITIVO POR QR-CODE
    const [validateInfoAlert, setValidateInfoAlert] = useState(false);
    const [validateInformation, setValidateInformation] = useState({ code: '123ABC' });

    // ALERTA GENÉRICO DE ERRO/AVISO
    const [warnAlert, setWarnAlert] = useState({ show: false, title: null, text: null });


    // RESTAURA O CPF DIGITADO ANTERIORMENTE (ex: cliente voltou da tela de cadastro)
    useEffect(() => {
        const savedDocument = sessionStorage.getItem('CLIENT_DOCUMENT');

        if (savedDocument) {
            setClientDocument(JSON.parse(savedDocument));
        }
    }, []);


    const saveClientData = (data) => {
        const sanitized = sanitizeClientSessionData(data);

        storage.save('CLIENT_DATA', sanitized); // ATUALIZA OS VALORES DE CLIENTE NO LOCAL STORAGE

        clientArray.current = sanitized;

        setClientData(sanitized);
    };


    // BUSCA A COMANDA DO CLIENTE A PARTIR DO CPF INFORMADO
    const findClientData = async () => {
        try {
            const validDocument = DocumentValidator(clientDocument);

            if (!validDocument) {
                setWarnAlert({ show: true, title: 'Alerta', text: 'Erro:\n\nDigite os dados corretamente antes de continuar!' });
                return;
            }

            const resAccount = await axios.post(`/smartmenu/client/command`, {
                companyId: companyData.id,
                document: clientDocument,
            });

            setRequireDocumentAlert(false);

            if (resAccount.status !== 200) return;

            const res = resAccount.data;

            if (res.message === 'FIND-COMMAND') {
                saveClientData(res.data);

                // REGISTRA QUE ESTE DISPOSITIVO JÁ FOI VALIDADO PARA ESTE CLIENTE
                const resCacheValidate = storage.get('INFO_TO_VALIDATE');

                if (resCacheValidate && resCacheValidate.uuid === validateInformation.uuid) {
                    const validatedDevices = storage.get('CLIENT_VALIDATION') ?? [];
                    const validation = { id: validateInformation.id, uuid: validateInformation.uuid };

                    storage.saveWithTTL('CLIENT_VALIDATION', [...validatedDevices, validation], 43200);
                }

                storage.exclude('INFO_TO_VALIDATE');

                await makeOrder();

            } else if (res.message === 'NOT-FOUND') {
                clientArray.current = {
                    id: null,
                    name: null,
                    document: clientDocument,
                    phone: null,
                    birth_date: null,
                    older: null,
                };

                setRegisterAlert(true);

            } else if (res.message === 'FIND-CLIENT') {
                const analize = await analyzeDateBirth(res.data.birth_date);

                clientArray.current = { ...res.data, older: analize.valid };

                await analyzeData(clientArray.current);
            }

        } catch (error) {
            console.error('ERROR - FIND CLIENT DATA FUNCTION:', error);
            setWarnAlert({ show: true, title: 'Erro', text: error?.response?.data?.error });
        }
    };


    // DECIDE A COMANDA DO CLIENTE: VALIDA O DISPOSITIVO E CRIA/REAPROVEITA A COMANDA NO BACKEND
    const analyzeData = async (data) => {
        // SÓ PERGUNTA A QUANTIDADE DE PESSOAS UMA VEZ POR SESSÃO DE CARRINHO
        // (ver specs/IMPLEMENTANDO-QTD-PESSOAS-COMANDAS.spec.md, Fase 7)
        if (!peopleCountAsked.current) {
            pendingAnalyzeClient.current = data;
            setPeopleCountAlert(true);
            return;
        }

        try {
            const client = data;

            if (client.id) {
                const validatedDevices = storage.get('CLIENT_VALIDATION');
                const deviceValidated = validateUUID({ id: client.id, hash: client.uuid, cache: validatedDevices });

                if (!deviceValidated) {
                    storage.saveWithTTL('INFO_TO_VALIDATE', client, 1800);

                    const code = await generateValidateInformation({ client: { ...client, location }, companyId: companyData.id });

                    setValidateInformation({ ...client, code });
                    setValidateInfoAlert(true);
                    return;
                }
            }

            if (!client.name) {
                client.name = clientName;
            }

            setLoading(true);

            const resCommand = await axios.post(`/smartmenu/command/validate`, {
                companyId: companyData.id,
                data: client,
                location,
                peopleCount,
            });

            if (resCommand.status === 200) {
                saveClientData(resCommand.data.data);
                await makeOrder();
            }

        } catch (error) {
            console.error('ERROR - ANALIZE CLIENT DATA FUNCTION:', error);
            setWarnAlert({ show: true, title: 'Erro', text: error?.response?.data?.error });

        } finally {
            setLoading(false);
        }
    };


    // VALIDA A COMANDA, FILTRA PRODUTOS RESTRITOS E ENVIA O PEDIDO PARA A API
    const makeOrder = async () => {
        try {
            setLoading(true);
            setOlderAlert(false);

            const productsList = [...products];
            const client = clientArray.current;

            if (orderTypeValue === 'COMMAND') {
                const hasValidOrder = validateOrder(client, companyData);
                const isCommandOpen = await checkCommandStatus(client, companyData);

                if (!hasValidOrder || !isCommandOpen) {
                    storage.exclude('CLIENT_DATA');
                    setClientData(null);
                    setRequireDocumentAlert(true);
                    return;
                }

                const restriction = restrictionController({
                    restriction: companyData.restriction,
                    client: clientArray.current,
                    products: cart.current.products,
                    deleteProduct: handleDeleteProduct,
                });

                if (restriction.status === 201) {
                    setWarnAlert({ show: true, title: 'Alerta', text: restriction.message });
                }
            }

            if (productsList.length === 0) {
                setWarnAlert({
                    show: true,
                    title: 'Alerta',
                    text: 'Nenhum produto no carrinho para realizar o pedido! Selecione os produtos e depois retorne aqui para completar seu pedido.',
                });

                clearCart();
                return;
            }

            await saveOrder({
                companyId: companyData.id,
                cart: cart.current,
                client,
                products: productsList,
                type: orderTypeValue,
            });

            storage.exclude('CART');
            navigate('account');

        } catch (error) {
            console.error('ERROR - SAVE ORDER IN DB:', error);
            setWarnAlert({
                show: true,
                title: 'ERRO',
                text: error?.response?.data?.error ?? 'Não foi possível realizar seu pedido.\nPeça ajuda a um colaborador para solucionar o erro o mais rápido possível!',
            });

        } finally {
            setLoading(false);
        }
    };


    return {
        requireDocumentAlert, setRequireDocumentAlert,
        clientDocument, setClientDocument,

        registerAlert, setRegisterAlert,
        clientNameAlert, setClientNameAlert,
        clientName, setClientName,
        olderAlert, setOlderAlert,

        orderTypeAlert, setOrderTypeAlert,
        orderTypeValue, setOrderTypeValue,
        clientPhoneAlert, setClientPhoneAlert,
        clientPhone, setClientPhone,

        peopleCount, setPeopleCount,
        peopleCountAlert, setPeopleCountAlert,
        peopleCountAsked,
        pendingAnalyzeClient,

        validateInfoAlert, setValidateInfoAlert,
        validateInformation,

        warnAlert, setWarnAlert,

        findClientData,
        analyzeData,
        makeOrder,
    };
}
